import React from 'react';
import './Frontpage.css';
import Footer from '../components/Footer';
import { Zap, Code, Terminal, ChevronRight } from 'lucide-react';
import Logo from '../components/Logo';

interface Props {
  onGetStarted: () => void;
  onLogin: () => void;
}

const Frontpage: React.FC<Props> = ({ onGetStarted, onLogin }) => {
  return (
    <div className="frontpage">
      <div className="hero">
        <div className="hero-content">
          <h1>Codenest — <br />AI Code Studio</h1>
          <p className="lead">
            The free online code compiler and AI-powered web IDE that turns your prompts into
            production-ready code. Write, run, and debug JavaScript, Python, TypeScript, and more —
            all directly in your browser. No setup. No installs.
          </p>
          <div className="hero-actions">
            <button className="primary" onClick={onGetStarted}>
              Get Started <ChevronRight size={18} style={{ marginLeft: 8 }} />
            </button>
            <button className="ghost" onClick={onLogin}>Sign in</button>
          </div>
        </div>

        <div className="hero-art">
          <div className="core-outer"></div>
          <div className="core-3d-container">
            <div className="core-3d-hex hex-1"></div>
            <div className="core-3d-hex hex-2"></div>
            <div className="core-3d-hex hex-3"></div>
            <div className="core-inner-glow"></div>
            <div style={{ position: 'absolute', transform: 'translateZ(60px)', top: '50%', left: '50%', marginLeft: -40, marginTop: -40 }}>
              <Logo size={80} />
            </div>
          </div>
        </div>
      </div>

      <div className="features">
        <h2 className="features-heading">Everything you need to code faster</h2>
        <div className="feature">
          <div className="feature-icon">
            <Zap size={24} />
          </div>
          <h3>Fast Multi-language Runner</h3>
          <p>Run JavaScript, Python, TypeScript, and more instantly in your browser. No server setup — our online code runner executes your code in seconds.</p>
        </div>

        <div className="feature">
          <div className="feature-icon">
            <Code size={24} />
          </div>
          <h3>AI Code Generation</h3>
          <p>Powered by OpenRouter AI, CodeNest converts natural language prompts into clean, idiomatic code — your personal AI coding assistant in the browser.</p>
        </div>

        <div className="feature">
          <div className="feature-icon">
            <Terminal size={24} />
          </div>
          <h3>Cinematic Terminal</h3>
          <p>A VS Code-inspired browser terminal for debugging, output inspection, and real-time feedback. The most beautiful web IDE terminal you'll ever use.</p>
        </div>
      </div>

      <div className="limitations">
        <h2>Why choose CodeNest?</h2>
        <p>Many online compilers suffer from restricted libraries, single‑file limits, and tight execution caps. CodeNest tackles those obstacles with a robust browser IDE that supports real project structures, lets you add dependencies, and runs code in a scalable sandbox so you can build more complex apps without hitting invisible walls.</p>
        <p className="comparison">Looking beyond the competition, Replit is often cited as the leader because it combines those same strengths with realtime collaboration, broad language support, and an actual container per user—effectively a full cloud IDE. You can clone repos, install packages, and work with teams in the browser; CodeNest strives to deliver a similarly powerful experience optimized for coding tutorials and quick experiments.</p>
      </div>

      <Footer />
    </div>
  );
};

export default Frontpage;
